/**
 * Optimizes Cloudinary image URLs for avatar display by adding transformations.
 * Resizes to 256x256 and applies quality optimization.
 */
const optimizeCloudinaryUrl = (url) => {
    // Check if this is a Cloudinary URL
    if (!url.includes('cloudinary.com')) {
        return url;
    }
    // Cloudinary URL format: https://res.cloudinary.com/{cloud_name}/image/upload/{transformations}/{path}
    // We want to insert transformations after /upload/
    const uploadIndex = url.indexOf('/upload/');
    if (uploadIndex === -1) {
        return url;
    }
    // Insert transformation: w_256,h_256,c_fill,q_auto,f_auto
    // - w_256,h_256: resize to 256x256
    // - c_fill: crop to fill the dimensions
    // - q_auto: automatic quality optimization
    // - f_auto: automatic format selection (WebP when supported)
    const transformation = 'w_256,h_256,c_fill,q_auto,f_auto';
    const beforeUpload = url.substring(0, uploadIndex + 8); // includes '/upload/'
    const afterUpload = url.substring(uploadIndex + 8);
    return `${beforeUpload}${transformation}/${afterUpload}`;
};
/**
 * Determines how to display a user's avatar based on available data.
 * Priority: avatar field → first letter of name → generic fallback
 *
 * @param user - The session user object
 * @returns Avatar display configuration
 */
export const getAvatarDisplay = (user) => {
    if (!user) {
        return { type: 'fallback' };
    }
    // Priority 1: Custom uploaded avatar
    if (user.avatar && user.avatar.trim().length > 0) {
        return { type: 'image', url: optimizeCloudinaryUrl(user.avatar) };
    }
    // Priority 2: OAuth provider image
    if (user.image && user.image.trim().length > 0) {
        return { type: 'image', url: user.image };
    }
    // Priority 3: First letter of name (Initial Circle)
    if (user.name && user.name.trim().length > 0) {
        const firstLetter = user.name.trim()[0].toUpperCase();
        return { type: 'initial', letter: firstLetter };
    }
    // Priority 4: Generic fallback
    return { type: 'fallback' };
};
